import {
  type BackgammonEndpoint,
  type BackgammonPosition,
  type BackgammonTurn,
  type BlotRisk,
  movableSources,
  stepRisks,
} from '@strategy4kids/game-engine';
import { useMemo } from 'react';
import { useThemeStore } from '../state/theme-store.js';

export interface BackgammonHints {
  /** Points (or `'bar'`) holding a checker that can move now; null when off. */
  readonly movable: BackgammonEndpoint[] | null;
  /** Destinations of the selected checker that would leave a blot exposed; null when off. */
  readonly risks: Map<BackgammonEndpoint, BlotRisk> | null;
}

/**
 * Teaching-aid overlays for the backgammon board, driven by the same two theme
 * toggles as chess ("Highlight movable pieces", "Warn about unsafe moves").
 * `canMove` is the screen's own "it is the human's turn to play the dice" test,
 * so nothing is ringed while the robot plays or a request is in flight.
 */
export function useBackgammonHints(
  position: BackgammonPosition | null,
  candidates: readonly BackgammonTurn[],
  playedCount: number,
  selected: BackgammonEndpoint | null,
  canMove: boolean,
): BackgammonHints {
  const highlightMovable = useThemeStore((s) => s.highlightMovable);
  const warnUnsafeMoves = useThemeStore((s) => s.warnUnsafeMoves);
  const movable = useMemo(
    () => (highlightMovable && canMove ? movableSources(candidates, playedCount) : null),
    [highlightMovable, canMove, candidates, playedCount],
  );
  const risks = useMemo(
    () =>
      warnUnsafeMoves && canMove && selected != null && position != null
        ? stepRisks(position, candidates, playedCount, selected)
        : null,
    [warnUnsafeMoves, canMove, selected, position, candidates, playedCount],
  );
  return { movable, risks };
}
